"use client";

import { useState, useEffect, useCallback } from "react";
import type {
  SupplierEvaluationProject,
  AssessmentForm,
  SEWorkflowState,
  SupplierCollaborator,
  EvaluatorUser,
} from "../types";
import {
  getEvaluation,
  getUserEvaluations,
  updateEvaluation,
  deleteEvaluation,
  transitionState,
  saveAssessmentForm,
  getAssessmentForm,
  saveSupplierDraftResponses,
  saveSupplierResponses,
  getSupplierResponses,
  getAnalysisReport,
  validateSupplierToken,
  addCollaborator,
  removeCollaborator,
  touchCollaboratorActivity,
  addEvaluatorUser,
  removeEvaluatorUser,
} from "@/platform/firebase/firestore/supplier-evaluation";

export {
  validateSupplierToken,
  touchCollaboratorActivity,
  saveSupplierDraftResponses,
  saveSupplierResponses,
  getSupplierResponses,
  getAnalysisReport,
  getAssessmentForm,
  saveAssessmentForm,
};

// ─── Single project hook ──────────────────────────────────────────────────────
export function useSupplierEvaluation(evaluationId: string | null) {
  const [project, setProject]           = useState<SupplierEvaluationProject | null>(null);
  const [assessmentForm, setAssessmentForm] = useState<AssessmentForm | null>(null);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!evaluationId) return;
    setLoading(true);
    setError(null);
    try {
      const [proj, form] = await Promise.all([
        getEvaluation(evaluationId),
        getAssessmentForm(evaluationId),
      ]);
      setProject(proj);
      setAssessmentForm(form);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load evaluation");
    } finally {
      setLoading(false);
    }
  }, [evaluationId]);

  useEffect(() => { void load(); }, [load]);

  const doTransition = useCallback(async (
    newState: SEWorkflowState,
    extra?: Record<string, unknown>,
  ) => {
    if (!evaluationId) return;
    setSaving(true);
    try {
      await transitionState(evaluationId, newState, extra);
      setProject((p) => p ? { ...p, workflow_state: newState, ...extra } : p);
    } finally {
      setSaving(false);
    }
  }, [evaluationId]);

  const doUpdate = useCallback(async (data: Partial<SupplierEvaluationProject>) => {
    if (!evaluationId) return;
    setSaving(true);
    try {
      await updateEvaluation(evaluationId, data);
      setProject((p) => p ? { ...p, ...data } : p);
    } finally {
      setSaving(false);
    }
  }, [evaluationId]);

  const doSaveForm = useCallback(async (form: AssessmentForm) => {
    if (!evaluationId) return;
    await saveAssessmentForm(evaluationId, form);
    setAssessmentForm(form);
  }, [evaluationId]);

  const doDelete = useCallback(async () => {
    if (!evaluationId) return;
    await deleteEvaluation(evaluationId);
  }, [evaluationId]);

  const doAddCollaborator = useCallback(async (c: SupplierCollaborator) => {
    if (!evaluationId) return;
    await addCollaborator(evaluationId, c);
    setProject((p) => p ? { ...p, shared_with: [...(p.shared_with ?? []), c] } : p);
  }, [evaluationId]);

  const doRemoveCollaborator = useCallback(async (c: SupplierCollaborator) => {
    if (!evaluationId) return;
    await removeCollaborator(evaluationId, c);
    setProject((p) => p ? { ...p, shared_with: (p.shared_with ?? []).filter((x) => x.email !== c.email) } : p);
  }, [evaluationId]);

  const doAddEvaluator = useCallback(async (evaluator: EvaluatorUser) => {
    if (!evaluationId) return;
    await addEvaluatorUser(evaluationId, evaluator);
    setProject((p) => p ? { ...p, evaluator_users: [...(p.evaluator_users ?? []), evaluator] } : p);
  }, [evaluationId]);

  const doRemoveEvaluator = useCallback(async (evaluator: EvaluatorUser) => {
    if (!evaluationId) return;
    await removeEvaluatorUser(evaluationId, evaluator);
    setProject((p) => p ? { ...p, evaluator_users: (p.evaluator_users ?? []).filter((u) => u.uid !== evaluator.uid) } : p);
  }, [evaluationId]);

  return {
    project, setProject,
    assessmentForm, setAssessmentForm,
    loading, saving, error,
    reload: load,
    transition: doTransition,
    update: doUpdate,
    saveForm: doSaveForm,
    deleteEvaluation: doDelete,
    addCollaborator: doAddCollaborator,
    removeCollaborator: doRemoveCollaborator,
    addEvaluator: doAddEvaluator,
    removeEvaluator: doRemoveEvaluator,
  };
}

// ─── User projects list hook ──────────────────────────────────────────────────
export function useUserEvaluations(userId: string | null, userEmail?: string) {
  const [projects, setProjects] = useState<SupplierEvaluationProject[]>([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    getUserEvaluations(userId, userEmail)
      .then(setProjects)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId, userEmail]);

  const remove = (id: string) =>
    setProjects((prev) => prev.filter((p) => p.evaluation_id !== id));

  return { projects, loading, remove };
}
